import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import { orderKeys } from "@/lib/query";

export interface Order {
  id: string;
  customerId: string;
  customerName: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  totalCents: number;
  itemCount: number;
}

export interface OrderLineItem {
  id: string;
  productId: string;
  productName: string;
  productSku: string;
  quantity: number;
  unitPriceCents: number;
  createdAt: string;
}

interface OrderDetail {
  id: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  lineItems: OrderLineItem[];
  totalCents: number;
}

interface OrdersListResponse {
  data: Order[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export function useOrders(query: DataGridQuery, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...orderKeys.list(query), pageRequest] as const,
    queryFn: async (): Promise<OrdersListResponse> => {
      const res = await fetch(`/api/orders?${buildListParams(query, pageRequest)}`);
      if (!res.ok) throw new Error("Failed to fetch orders");
      return res.json();
    },
  });
}

export function useOrder(orderId: string) {
  return useQuery({
    queryKey: orderKeys.detail(orderId),
    queryFn: async (): Promise<OrderDetail> => {
      const res = await fetch(`/api/orders/${orderId}`);
      if (!res.ok) throw new Error("Failed to fetch order");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { customerId: string; status?: string }): Promise<Order> => {
      const res = await fetch("/api/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Failed to create order");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      toast.success("Order created");
    },
    onError: () => {
      toast.error("Failed to create order");
    },
  });
}

export function useDeleteOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const res = await fetch(`/api/orders/${orderId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      toast.success("Order deleted");
    },
    onError: () => {
      toast.error("Failed to delete order");
    },
  });
}

export function useUpdateOrder(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: { status?: string }) => {
      const res = await fetch(`/api/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      toast.success("Order updated");
    },
    onError: () => {
      toast.error("Failed to update order");
    },
  });
}

export function useAddOrderItem(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (lineItem: { productId: string; quantity: number }) => {
      const res = await fetch(`/api/orders/${orderId}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(lineItem),
      });
      if (!res.ok) throw new Error("Failed to add line item");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      toast.success("Line item added");
    },
    onError: () => {
      toast.error("Failed to add line item");
    },
  });
}

export function useRemoveOrderItem(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderItemId: string) => {
      const res = await fetch(`/api/orders/${orderId}/items/${orderItemId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      toast.success("Line item removed");
    },
    onError: () => {
      toast.error("Failed to remove line item");
    },
  });
}
